import React from "react"

import V2ChromeLayout from "@utils/v2ChromeLayout"

/**
 * /developers — the discoverable entry point for MoonClerk's developer surface (T-079).
 *
 * Filed from the is-agentic.com readiness scan: the API and its documentation both exist,
 * but nothing on the marketing site linked either, and `/docs`, `/api` and `/developers`
 * all 404'd. Agents and developers had no path in from moonclerk.com.
 *
 * **Every capability claimed here was verified 2026-08-21.** The constraint that shaped
 * the copy: the interactive demo on /features renders `import { MoonClerk } from
 * "@moonclerk/node"` plus Ruby and Python snippets, and **those SDKs do not exist** —
 * `@moonclerk/node` and `moonclerk` are both absent from npm, `moonclerk` is absent from
 * PyPI, and the GitHub org publishes no SDK repositories. The one real package is a
 * third-party rubygem (`moonclerk` 1.0.3, TrevorHinesley, last published 2015-12-09),
 * which is unmaintained and not ours to point people at. So this page documents the REST
 * API directly and claims no client libraries. See the ticket filed against the /features
 * demo — do not "restore" SDK links here to match it.
 *
 * The page body is server-rendered; the shared nav and footer are not (they are injected
 * client-side into #mc-nav / #mc-footer by /assets/mc-chrome.js — see T-012). That is why
 * the in-body links below matter: a footer-only link would be invisible to any agent that
 * does not execute JavaScript.
 */

/**
 * Verbatim from the official docs at github.com/moonclerk/developer/blob/main/api/README.md.
 *
 * Do NOT "simplify" this to a `Bearer` token or add a `/v2/` path segment. The first draft
 * of this page copied its example from the /features demo widget, which shows
 * `curl https://api.moonclerk.com/v2/events -H "Authorization: Bearer $MOONCLERK_KEY"` —
 * and that is wrong three ways: the scheme is `Token token=`, not `Bearer`; there is no
 * `/v2/` prefix; and there is no `events` endpoint. The live API's own 401 body,
 * `HTTP Token: Access denied.`, is Rails' HTTP Token authentication, which corroborates the
 * scheme. The `Accept` header is required, not optional — it carries the API version.
 */
const API_EXAMPLE = `curl https://api.moonclerk.com/forms \\
  -H "Authorization: Token token=YOUR_API_KEY" \\
  -H "Accept: application/vnd.moonclerk+json;version=1"`

const capabilities = [
  {
    title: "Read the REST API",
    body: "A read-only, REST-style API over your forms, customers, and payments. Authenticate with an API key passed as an authorization token, and request a version through the Accept header.",
  },
  {
    title: "Receive webhooks",
    body: "Subscribe to MoonClerk events and have them delivered to your own endpoint, so your systems react the moment a payment succeeds, fails, or a subscription changes.",
  },
  {
    title: "Pre-fill checkouts",
    body: "Pass an amount, name, email, or your own custom field values into a payment form from a URL, so payers arrive at a checkout that is already filled in.",
  },
  {
    title: "Track payers through checkout",
    body: "Hook into the checkout lifecycle to attribute conversions in your own analytics, rather than inferring them after the fact.",
  },
  {
    title: "Restyle payment forms",
    body: "Apply custom CSS to any form so an embedded MoonClerk checkout matches the site around it, down to the pixel.",
  },
  {
    title: "Start from a template",
    body: "Two open-source Gatsby starters — one for payments, one for donations — build a landing page that already accepts money.",
  },
]

const resources = [
  {
    href: "/openapi.json",
    title: "OpenAPI 3.1 description",
    body: "Machine-readable spec for all six endpoints.",
  },
  {
    href: "https://github.com/moonclerk/developer",
    title: "Developer documentation",
    body: "The full API and webhook reference.",
  },
  {
    href: "https://help.moonclerk.com/en/articles/1239754-how-do-i-generate-an-api-key",
    title: "Generate an API key",
    body: "Account → Settings → API Key.",
  },
  {
    href: "https://help.moonclerk.com/en/articles/6613469-how-do-i-use-moonclerk-s-webhooks",
    title: "Using webhooks",
    body: "Event types, payloads, and delivery.",
  },
  {
    href: "https://github.com/moonclerk/gatsby-starter-payments",
    title: "Gatsby payments starter",
    body: "A deployable landing page that takes payments.",
  },
  {
    href: "https://github.com/moonclerk/gatsby-starter-donations",
    title: "Gatsby donations starter",
    body: "The same, tuned for recurring giving.",
  },
  {
    href: "https://help.moonclerk.com/en/collections/12828-for-developers",
    title: "Developer help articles",
    body: "Every developer-facing guide in one place.",
  },
]

const styles = `
  .dev-page { background: #fff; color: #0b1f2a; }

  .dev-hero { padding: 96px 32px 72px; border-bottom: 1px solid #e2e8f0; }
  .dev-hero-inner { max-width: 1100px; margin: 0 auto; }
  .dev-eyebrow {
    font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
    color: #0f766e; margin-bottom: 18px;
  }
  .dev-hero h1 {
    font-size: clamp(2.25rem, 4.5vw, 3.5rem); line-height: 1.05; letter-spacing: -.02em;
    margin: 0 0 20px; max-width: 20ch;
  }
  .dev-hero p.dev-lede {
    font-size: 18px; line-height: 1.65; color: #475569; margin: 0; max-width: 62ch;
  }

  .dev-code {
    margin-top: 36px; background: #0b1f2a; border-radius: 12px; padding: 22px 24px;
    overflow-x: auto; max-width: 100%;
  }
  .dev-code pre {
    margin: 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13.5px; line-height: 1.7; color: #cbd5e0; white-space: pre;
  }
  .dev-code-note { margin: 14px 0 0; font-size: 13px; color: #64748b; }

  .dev-band { padding: 80px 32px; }
  .dev-band-inner { max-width: 1100px; margin: 0 auto; }
  .dev-band h2 {
    font-size: clamp(1.6rem, 2.6vw, 2.25rem); line-height: 1.12; letter-spacing: -.02em;
    margin: 0 0 12px;
  }
  .dev-band-lede { font-size: 16px; line-height: 1.65; color: #475569; margin: 0 0 44px; max-width: 60ch; }

  .dev-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 900px) { .dev-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .dev-grid { grid-template-columns: 1fr; } }

  .dev-cap h3 { font-size: 16px; font-weight: 700; margin: 0 0 8px; line-height: 1.3; }
  .dev-cap p { font-size: 14px; line-height: 1.7; color: #475569; margin: 0; }
  .dev-cap { border-top: 2px solid #0f766e; padding-top: 18px; }

  .dev-resources { background: #f8fafc; border-top: 1px solid #e2e8f0; }
  .dev-res-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;
    list-style: none; margin: 0; padding: 0;
  }
  .dev-res-grid > li { display: flex; }
  .dev-res-grid > li > .dev-res { width: 100%; }
  @media (max-width: 900px) { .dev-res-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .dev-res-grid { grid-template-columns: 1fr; } }

  .dev-res {
    display: block; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: 22px; text-decoration: none; color: inherit;
    transition: border-color .2s, transform .2s, box-shadow .2s;
  }
  .dev-res:hover, .dev-res:focus-visible {
    border-color: #0f766e; transform: translateY(-2px);
    box-shadow: 0 12px 28px -18px rgba(15, 118, 110, .35);
  }
  .dev-res strong { display: block; font-size: 15px; margin-bottom: 6px; }
  .dev-res span { font-size: 13.5px; color: #475569; line-height: 1.6; }

  .dev-limits { padding: 80px 32px; border-top: 1px solid #e2e8f0; }
  .dev-limits ul { margin: 0; padding: 0; list-style: none; max-width: 70ch; }
  .dev-limits li {
    font-size: 15px; line-height: 1.7; color: #475569;
    padding: 14px 0 14px 26px; border-bottom: 1px solid #eef2f6; position: relative;
  }
  .dev-limits li::before {
    content: ""; position: absolute; left: 0; top: 22px;
    width: 8px; height: 8px; border-radius: 50%; background: #f59e0b;
  }
  .dev-limits li b { color: #0b1f2a; font-weight: 650; }
`

const DevelopersPage = () => (
  <V2ChromeLayout
    title="MoonClerk for Developers | REST API & Webhooks"
    description="Build on MoonClerk with the read-only REST API, webhooks, checkout pre-fill, custom form CSS, and open-source Gatsby starters. Full developer documentation and API key setup."
    canonicalPath="/developers"
    pageClassName="dev-page"
  >
    <style>{styles}</style>

    <section className="dev-hero">
      <div className="dev-hero-inner">
        <p className="dev-eyebrow">Developers</p>
        <h1>Build on top of your MoonClerk account</h1>
        <p className="dev-lede">
          MoonClerk handles the checkout, the recurring billing, and the
          failed-payment recovery. When you need that data in your own systems —
          or need a form to behave like part of your product — there is a REST
          API, webhooks, and a set of checkout hooks to reach for.
        </p>

        <div className="dev-code">
          <pre>{API_EXAMPLE}</pre>
        </div>
        <p className="dev-code-note">
          Generate an API key at{" "}
          <a href="https://app.moonclerk.com/settings/api-key" rel="noopener">
            app.moonclerk.com/settings/api-key
          </a>
          . Endpoints are <code>/forms</code>, <code>/customers</code> and{" "}
          <code>/payments</code>, each supporting a list and a single-record
          read.
        </p>
      </div>
    </section>

    <section className="dev-band">
      <div className="dev-band-inner">
        <h2>What you can build</h2>
        <p className="dev-band-lede">
          Every capability below is available on every MoonClerk plan — there is
          no developer tier and no per-call pricing.
        </p>
        <div className="dev-grid">
          {capabilities.map((capability) => (
            <div className="dev-cap" key={capability.title}>
              <h3>{capability.title}</h3>
              <p>{capability.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="dev-band dev-resources">
      <div className="dev-band-inner">
        <h2>Documentation and reference</h2>
        <p className="dev-band-lede">
          The complete API and webhook reference lives in the developer
          documentation repository. The guides below cover the setup steps
          around it.
        </p>
        {/*
          A <ul> of links rather than a grid of bare anchors. Two reasons, and the second
          is not obvious: a list of links is what this is semantically, and the AI-corpus
          generator only reads <p> and <li> content (see `collectProseSectionsFromHtml` in
          script/generate-v2-ai-corpus.js). Anchors floating in a <div> are invisible to
          it, so the markdown twin of this page would list its headings and omit every
          resource — which is exactly the defect this page was created to fix.
        */}
        <ul className="dev-res-grid">
          {resources.map((resource) => (
            <li key={resource.href}>
              <a className="dev-res" href={resource.href} rel="noopener">
                <strong>{resource.title}</strong>
                <span>{resource.body}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="dev-limits">
      <div className="dev-band-inner">
        <h2>Before you start</h2>
        <p className="dev-band-lede">
          Three things worth knowing up front, so you do not design around a
          capability that is not there.
        </p>
        {/*
          Currency was deliberately dropped from this list. An earlier draft asserted "one
          currency per account", sourced from a Help Center article last updated over eight
          years ago. That conflates two different things: a MoonClerk account is denominated
          in a single currency, but a payer holding a card in another currency can still
          check out, with the conversion happening outside MoonClerk. Stated as a flat
          limitation it reads as "we cannot take international payments", which is wrong and
          would talk away business we actually serve. It is also not an API constraint, so
          it does not belong on this page at all — the nuance lives in llms.txt instead.
        */}
        <ul>
          <li>
            <b>The API is read-only.</b> Every operation is a <code>GET</code>{" "}
            over forms, customers, and payments. Creating and editing forms,
            plans, and pricing happens in the MoonClerk interface.
          </li>
          <li>
            <b>MoonClerk runs on Stripe.</b> A Stripe account is required.
            Payouts, per-transaction fees, and the list of supported countries
            are Stripe's.
          </li>
          <li>
            <b>There are no official client libraries.</b> Call the API directly
            over HTTP. Any MoonClerk package you find on a language registry is
            third-party and unmaintained.
          </li>
        </ul>
      </div>
    </section>
  </V2ChromeLayout>
)

export default DevelopersPage
